import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Product } from '../models/DB';
import { useStoreConfig } from '../contexts/StoreConfigContext';
import { calculateDiscountedPrice } from '../utils/customFunctions';

interface ProductMetaTagsProps {
    product: Product;
}

export const ProductMetaTags: React.FC<ProductMetaTagsProps> = ({ product }) => {
    const { config } = useStoreConfig();

    const siteName = config.companyName || 'Cosmo Cart';
    const title = `${product.name} - ${config.companyName}`;
    const url = typeof window !== 'undefined' ? window.location.href : '';
    
    // Calculate final price (with discount if applicable)
    const finalPrice = product.discount > 0
        ? parseFloat(calculateDiscountedPrice(product.price, product.discount))
        : product.price;
    
    // Build description with price and availability
    const priceText = product.discount > 0
        ? `${config.currency}${finalPrice.toFixed(2)} (${product.discount}% off - was ${config.currency}${product.price.toFixed(2)})`
        : `${config.currency}${finalPrice.toFixed(2)}`;
    
    const stockText = product.inStock ? 'In Stock' : 'Out of Stock';
    
    const description = `${product.description || product.name} | ${priceText} | ${stockText} | ${product.category}`;
    
    // Get primary product image from JSONB object
    // Images are stored as: { "image1": { "url": "..." }, "thumbnail": { "url": "..." } }
    let productImage = config.appLogo; // Default fallback

    if (product.images && typeof product.images === 'object') {
        // Prefer thumbnail for social sharing (optimized size)
        if ('thumbnail' in product.images) {
            const thumbnailData = product.images.thumbnail;
            if (thumbnailData && typeof thumbnailData === 'object' && 'url' in thumbnailData) {
                productImage = thumbnailData.url;
            }
        } else {
            // Fallback to first image (image1, image2, etc.)
            const imageKeys = Object.keys(product.images).filter(key => key.startsWith('image'));
            if (imageKeys.length > 0) {
                const firstImageKey = imageKeys.sort()[0];
                const imageData = product.images[firstImageKey as keyof typeof product.images];
                if (imageData && typeof imageData === 'object' && 'url' in imageData) {
                    productImage = imageData.url;
                }
            }
        }
    }
    
    // Product type for schema.org
    const availability = product.inStock 
        ? 'https://schema.org/InStock' 
        : 'https://schema.org/OutOfStock';

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{title}</title>
            <meta name='description' content={description} />
            
            {/* Open Graph / Facebook / WhatsApp / LinkedIn */}
            <meta property='og:type' content='product' />
            <meta property='og:url' content={url} />
            <meta property='og:title' content={title} />
            <meta property='og:description' content={description} />
            <meta property='og:site_name' content={siteName} />
            
            {/* Product Image */}
            <meta property='og:image' content={productImage} />
            <meta property='og:image:secure_url' content={productImage} />
            <meta property='og:image:type' content='image/jpeg' />
            <meta property='og:image:alt' content={product.name} />
            
            {/* Product-specific OG tags */}
            <meta property='product:price:amount' content={finalPrice.toFixed(2)} />
            <meta property='product:price:currency' content={config.currency} />
            <meta property='product:availability' content={product.inStock ? 'in stock' : 'out of stock'} />
            <meta property='product:condition' content='new' />
            <meta property='product:category' content={product.category} />
            {product.discount > 0 && (
                <meta property='product:sale_price:amount' content={finalPrice.toFixed(2)} />
            )}
            
            {/* Twitter */}
            <meta property='twitter:card' content='summary_large_image' />
            <meta property='twitter:url' content={url} />
            <meta property='twitter:title' content={title} />
            <meta property='twitter:description' content={description} />
            <meta property='twitter:image' content={productImage} />
            
            {/* Schema.org Product Structured Data */}
            <script type='application/ld+json'>
                {JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'Product',
                    name: product.name,
                    description: product.description || product.name,
                    image: productImage,
                    brand: {
                        '@type': 'Brand',
                        name: config.companyName
                    },
                    offers: {
                        '@type': 'Offer',
                        url: url,
                        priceCurrency: config.currency,
                        price: finalPrice.toFixed(2),
                        availability: availability,
                        priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
                        itemCondition: 'https://schema.org/NewCondition'
                    },
                    sku: product.code,
                    category: product.category,
                    ...(product.showRating !== false && {
                        aggregateRating: {
                            '@type': 'AggregateRating',
                            ratingValue: '5',
                            bestRating: '5',
                            worstRating: '1',
                            ratingCount: '1'
                        }
                    })
                })}
            </script>
        </Helmet>
    );
};
